import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import icon from "../../assets/image/png/chatbot.png";
import { useLocation } from "react-router-dom";

const ChatBot = ({ closeChat }) => {
  const [messages, setMessages] = useState([]);
  const [userMessage, setUserMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState({});
  const messagesEndRef = useRef(null);
  const location = useLocation();

  // Responsive dimensions
  const [dimensions, setDimensions] = useState({
    width: "360px",
    height: "520px"
  });

  // Detect page context
const pageContext = location.pathname.includes("/dsk")
  ? "dsk"
  : "general";
  // Update dimensions based on screen size
  useEffect(() => {
    const updateDimensions = () => {
      const width = window.innerWidth;
      if (width <= 360) { // Mobile
        setDimensions({
          width: "95vw",
          height: "80vh",
          maxWidth: "270px",
          maxHeight: "60vh"
        });
      }
      else if (width <= 420) { // Mobile
        setDimensions({
          width: "95vw",
          height: "80vh",
          maxWidth: "300px",
          maxHeight: "60vh"
        });
      } else if (width <= 480) { // Mobile
        setDimensions({
          width: "95vw",
          height: "80vh",
          maxWidth: "350px",
          maxHeight: "60vh"
        });
      } else if (width <= 768) { // Tablet
        setDimensions({
          width: "400px",
          height: "600px",
          maxWidth: "90vw",
          maxHeight: "90vh"
        });
      } else { // Desktop
        setDimensions({
          width: "360px",
          height: "520px"
        });
      }
    };

    // Set initial dimensions
    updateDimensions();

    // Add event listener for window resize
    window.addEventListener("resize", updateDimensions);
    
    // Cleanup
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const sendMessage = async () => {
    if (!userMessage.trim()) return;

    const newMessages = [...messages, { type: "user", text: userMessage }];
    setMessages(newMessages);
    setUserMessage("");
    setLoading(true);

    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + `/chat`,
        {
          message: userMessage,
          page: pageContext, // 👈 THIS LINE
        }
      );
      // const response = await axios.post(
      //   `http://localhost:6002/chat`,
      //   {
      //     message: userMessage,
      //     page: pageContext, // 👈 THIS LINE
      //   }
      // );


      setMessages((prev) => [
        ...newMessages,
        { type: "bot", text: response.data.reply },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...newMessages,
        {
          type: "bot",
          text: "Sorry, there was an error. Please try again later.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const toggleExpand = (index) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleCall = () => window.open("tel:+919773571992", "_blank");
  const handleWhatsApp = () => {
    const phoneNumber = "+919773571992";
    const message = "Hello, I need help with something.";
    window.open(
      `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  };
  const handleEmail = () =>
    window.open("mailto:support@droneplanet.in", "_blank");

  // Get current screen size for conditional styling
  const isMobile = window.innerWidth <= 480;
  const isTablet = window.innerWidth > 480 && window.innerWidth <= 768;

  return (
    <div
      className="chatbot-container"
      style={{
        width: dimensions.width,
        height: dimensions.height,
        maxWidth: dimensions.maxWidth,
        maxHeight: dimensions.maxHeight,
        display: "flex",
        flexDirection: "column",
        borderRadius: isMobile ? "12px" : "20px",
        overflow: "hidden",
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        background: "#fff",
        fontFamily: "Arial, sans-serif",
        position: "relative",
        margin: isMobile ? "10px auto" : "0", // Center on mobile
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #308DCC 0%, #56B4F8 100%)",
          color: "#fff",
          padding: isMobile ? "10px 12px" : "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img 
            src={icon} 
            alt="icon" 
            style={{ 
              width: isMobile ? "22px" : "28px", 
              height: isMobile ? "22px" : "28px" 
            }} 
          />
          <h3 style={{ 
            fontSize: isMobile ? "14px" : "16px", 
            margin: 0,
            fontWeight: "500"
          }}>
            {pageContext === "dsk"
              ? "DSK Business Assistant"
              : "Drone Planet Support"}
          </h3>
        </div>
        <button
          onClick={closeChat}
          style={{
            background: "transparent",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            padding: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloseIcon fontSize={isMobile ? "small" : "medium"} />
        </button>
      </div>

      {/* Contact Buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          backgroundColor: "#f5f6fa",
          padding: isMobile ? "6px 4px" : "8px 0",
          flexShrink: 0,
          gap: isMobile ? "2px" : "0",
        }}
      >
        <div
          onClick={handleCall}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: isMobile ? "10px" : "12px",
            background: "#fff",
            padding: isMobile ? "4px 6px" : "5px 10px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            flex: 1,
            margin: isMobile ? "0 1px" : "0",
            minHeight: "28px"
          }}
        >
          <PhoneIcon style={{ 
            fontSize: isMobile ? "12px" : "16px", 
            marginRight: "3px" 
          }} />
          Call
        </div>
        <div
          onClick={handleWhatsApp}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: isMobile ? "10px" : "12px",
            background: "#fff",
            padding: isMobile ? "4px 6px" : "5px 10px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            flex: 1,
            margin: isMobile ? "0 1px" : "0",
            minHeight: "28px"
          }}
        >
          <WhatsAppIcon style={{ 
            fontSize: isMobile ? "12px" : "16px", 
            marginRight: "3px" 
          }} />
          WhatsApp
        </div>
        <div
          onClick={handleEmail}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: isMobile ? "10px" : "12px",
            background: "#fff",
            padding: isMobile ? "4px 6px" : "5px 10px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            flex: 1,
            margin: isMobile ? "0 1px" : "0",
            minHeight: "28px"
          }}
        >
          <EmailIcon style={{ 
            fontSize: isMobile ? "12px" : "16px", 
            marginRight: "3px" 
          }} />
          Email
        </div>
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          padding: isMobile ? "8px 10px" : "12px",
          overflowY: "auto",
          backgroundColor: "#fafbfc",
          minHeight: 0, // Important for flex child scrolling
        }}
      >
        {messages.map((msg, idx) => {
          const isBot = msg.type === "bot";
          const lines = msg.text.split("\n");
          const preview = lines.slice(0, 2).join("\n");
          const needsReadMore = lines.length > 2;

          return (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent: isBot ? "flex-start" : "flex-end",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  maxWidth: isMobile ? "90%" : "70%",
                  padding: isMobile ? "8px 12px" : "10px 14px",
                  borderRadius: "18px",
                  fontSize: isMobile ? "13px" : "14px",
                  background: isBot
                    ? "#e9e9eb"
                    : "linear-gradient(135deg, #308DCC 0%, #56B4F8 100%)",
                  color: isBot ? "#333" : "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                }}
              >
                {isBot && needsReadMore && !expandedMessages[idx]
                  ? preview
                  : msg.text}
                {isBot && needsReadMore && (
                  <span
                    onClick={() => toggleExpand(idx)}
                    style={{
                      color: "#308DCC",
                      cursor: "pointer",
                      display: "block",
                      marginTop: "5px",
                      fontSize: isMobile ? "11px" : "13px",
                      fontWeight: "500",
                    }}
                  >
                    {expandedMessages[idx] ? "Show less" : "Read more"}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {loading && (
  <div style={{ display: "flex", justifyContent: "flex-start" }}>
    <div
      style={{
        maxWidth: isMobile ? "90%" : "70%",
        padding: isMobile ? "8px 12px" : "10px 14px",
        borderRadius: "18px",
        fontSize: isMobile ? "13px" : "14px",
        background: "#e9e9eb",
        color: "#333",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        display: "flex",
        alignItems: "center",
        gap: "4px",
      }}
    >
      {/* typing indicator dots */}
      <span style={{
        width: '6px',
        height: '6px',
        backgroundColor: '#555',
        borderRadius: '50%',
        display: 'inline-block',
        animation: 'blink 1.4s infinite both',
        animationDelay: '0s'
      }} />
      <span style={{
        width: '6px',
        height: '6px',
        backgroundColor: '#555',
        borderRadius: '50%',
        display: 'inline-block',
        animation: 'blink 1.4s infinite both',
        animationDelay: '0.2s'
      }} />
      <span style={{
        width: '6px',
        height: '6px',
        backgroundColor: '#555',
        borderRadius: '50%',
        display: 'inline-block',
        animation: 'blink 1.4s infinite both',
        animationDelay: '0.4s'
      }} />
    </div>
  </div>
)}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: isMobile ? "6px 8px" : "8px",
          borderTop: "1px solid #ddd",
          background: "#fff",
          flexShrink: 0,
        }}
      >
        <input
          type="text"
          placeholder="Ask something..."
          value={userMessage}
          onChange={(e) => setUserMessage(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          style={{
            flex: 1,
            padding: isMobile ? "8px 12px" : "10px 16px",
            fontSize: isMobile ? "13px" : "14px",
            borderRadius: "20px",
            border: "1px solid #ccc",
            outline: "none",
            minWidth: 0, // Prevents input from overflowing
          }}
        />
        <button
          onClick={sendMessage}
          disabled={loading || !userMessage.trim()}
          style={{
            background: "linear-gradient(135deg, #308DCC 0%, #56B4F8 100%)",
            border: "none",
            color: "#fff",
            borderRadius: "50%",
            width: isMobile ? "32px" : "38px",
            height: isMobile ? "32px" : "38px",
            marginLeft: "6px",
            cursor:
              loading || !userMessage.trim() ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            opacity: (loading || !userMessage.trim()) ? 0.6 : 1,
          }}
        >
          <SendIcon fontSize={isMobile ? "small" : "medium"} />
        </button>
      </div>
    </div>
  );
};

export default ChatBot;